import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { formatMoney, CURRENCY_SYMBOLS } from "@/lib/constants";
import { toast } from "sonner";
import {
  ArrowLeft, Gavel, Trophy, Target, Package, Users, MapPin, Clock, Layers, Info,
  BadgeCheck, CheckCircle2, Send, Gift,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { EscrowPanel } from "@/components/EscrowPanel";

const statusColors = {
  open: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30",
  awarded: "bg-neon-lime/15 text-neon-lime border-neon-lime/30",
  completed: "bg-neon-lime/15 text-neon-lime border-neon-lime/30",
  closed: "bg-muted text-muted-foreground border-border",
  pending: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30",
  accepted: "bg-neon-lime/15 text-neon-lime border-neon-lime/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
};

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 py-3 border-b border-border last:border-0">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-sm mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function BidForm({ campaign, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const sym = CURRENCY_SYMBOLS[campaign.currency] || "";
  const [f, setF] = useState({ amount: "", deliverables: campaign.deliverables || "", estimated_reach: "", engagement_stats: "", content_concept: "", timeline: "", additional_services: "", note: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const canSubmit = Number(f.amount) > 0 && f.deliverables.trim();

  const submit = async () => {
    if (!canSubmit) { toast.error("Enter a valid bid amount and deliverables."); return; }
    setSaving(true);
    try {
      await api.post(`/campaigns/${campaign.campaign_id}/bids`, { ...f, amount: Number(f.amount) });
      toast.success("Bid submitted! The promoter will review it.");
      setOpen(false);
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit bid.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-bid-form-btn" className="w-full h-12 rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-bold text-base"><Gavel className="h-4 w-4 mr-1" /> Submit a bid</Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Submit your proposal</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Your bid amount * ({sym}{campaign.currency})</Label>
            <Input type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="bid-amount-input" placeholder={`Max budget: ${formatMoney(campaign.budget, campaign.currency)}`} className="bg-background border-border h-11" />
          </div>
          <div>
            <Label className="mb-2 block">Proposed deliverables *</Label>
            <Textarea value={f.deliverables} onChange={(e) => set("deliverables", e.target.value)} data-testid="bid-deliverables-input" className="bg-background border-border" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-2 block">Estimated reach</Label><Input value={f.estimated_reach} onChange={(e) => set("estimated_reach", e.target.value)} data-testid="bid-reach-input" placeholder="e.g. 80k" className="bg-background border-border h-11" /></div>
            <div><Label className="mb-2 block">Engagement</Label><Input value={f.engagement_stats} onChange={(e) => set("engagement_stats", e.target.value)} data-testid="bid-engagement-input" placeholder="e.g. 5.2%" className="bg-background border-border h-11" /></div>
          </div>
          <div><Label className="mb-2 block">Content concept</Label><Textarea value={f.content_concept} onChange={(e) => set("content_concept", e.target.value)} data-testid="bid-concept-input" placeholder="Your creative idea for this campaign…" className="bg-background border-border" /></div>
          <div><Label className="mb-2 block">Delivery timeline</Label><Input value={f.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="bid-timeline-input" placeholder="e.g. 5 days" className="bg-background border-border h-11" /></div>
          <div><Label className="mb-2 block">Additional services</Label><Input value={f.additional_services} onChange={(e) => set("additional_services", e.target.value)} data-testid="bid-additional-input" placeholder="e.g. Usage rights, extra stories" className="bg-background border-border h-11" /></div>
          <div><Label className="mb-2 block">Note to promoter</Label><Textarea value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="bid-note-input" className="bg-background border-border" /></div>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="submit-bid-btn" className="w-full h-11 rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-bold"><Send className="h-4 w-4 mr-1" /> {saving ? "Submitting…" : "Submit bid"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HamperApplyForm({ campaign, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ pitch: "", shipping_address: "", deliverables_agreed: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const canSubmit = f.pitch.trim() && f.shipping_address.trim() && f.deliverables_agreed;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/campaigns/${campaign.campaign_id}/bids`, f);
      toast.success("Application sent! The promoter will review it.");
      setOpen(false);
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-hamper-apply-btn" className="w-full h-12 rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-bold text-base"><Gift className="h-4 w-4 mr-1" /> Apply for this hamper</Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Apply for the gift hamper</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-background p-4 text-sm">
            <p className="font-semibold mb-1">You'll receive: {formatMoney(campaign.hamper_value, campaign.currency)} hamper</p>
            {campaign.hamper_items && <p className="text-muted-foreground">{campaign.hamper_items}</p>}
          </div>
          <div><Label className="mb-2 block">Short pitch *</Label><Textarea value={f.pitch} onChange={(e) => set("pitch", e.target.value)} data-testid="hamper-pitch-input" placeholder="Why you're a great fit and how you'll feature the products…" className="bg-background border-border" /></div>
          <div><Label className="mb-2 block">Shipping address *</Label><Textarea value={f.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} data-testid="hamper-address-input" placeholder="Full name, street, city, state, PIN / ZIP, phone" className="bg-background border-border" /><p className="text-xs text-muted-foreground mt-1">Shared with the promoter only if they accept you.</p></div>
          <div className="rounded-xl border border-border bg-background p-4 text-sm">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Required deliverables</p>
            <p>{campaign.deliverables}</p>
          </div>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox checked={f.deliverables_agreed} onCheckedChange={(v) => set("deliverables_agreed", !!v)} data-testid="hamper-agree-checkbox" className="mt-0.5" />
            <span>I agree to create and publish the deliverables above in exchange for the gift hamper.</span>
          </label>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="submit-hamper-apply-btn" className="w-full h-11 rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-bold"><Send className="h-4 w-4 mr-1" /> {saving ? "Sending…" : "Send application"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BidCard({ bid, campaign, onAccept, isPromoter, i }) {
  const [accepting, setAccepting] = useState(false);
  const isHamper = bid.bid_type === "hamper";
  const act = async (action) => {
    setAccepting(true);
    try {
      await api.post(`/bids/${bid.bid_id}/${action}`);
      toast.success(action === "reject" ? "Application declined." : isHamper ? "Creator accepted for the hamper!" : "Creator selected! Bid accepted.");
      onAccept();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setAccepting(false); }
  };
  const accept = () => act("accept");
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      className={`rounded-2xl border p-5 ${bid.status === "accepted" ? "border-neon-lime/40 bg-neon-lime/5" : "border-border bg-surface"}`} data-testid={`bid-${bid.bid_id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={bid.influencer_picture} alt="" className="h-11 w-11 rounded-full object-cover bg-muted shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold truncate">{bid.influencer_name}</p>
            <p className="text-xs text-muted-foreground">
              {bid.influencer_followers ? `${Number(bid.influencer_followers).toLocaleString()} followers` : "Creator"}
              {bid.influencer_creator_type ? ` · ${bid.influencer_creator_type}` : ""}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          {isHamper ? <p className="font-heading font-bold text-sm flex items-center gap-1 justify-end"><Gift className="h-4 w-4 text-neon-pink" /> Hamper</p> : <p className="font-heading font-black text-xl">{formatMoney(bid.amount, bid.currency)}</p>}
          <Badge variant="outline" className={`capitalize ${statusColors[bid.status]}`}>{bid.status}</Badge>
        </div>
      </div>
      <div className="space-y-1.5 text-sm">
        {bid.pitch && <p data-testid={`bid-pitch-${bid.bid_id}`}><span className="text-muted-foreground">Pitch: </span>{bid.pitch}</p>}
        {isHamper && bid.shipping_address && <p data-testid={`bid-address-${bid.bid_id}`}><span className="text-muted-foreground">Ship to: </span><span className="whitespace-pre-line">{bid.shipping_address}</span></p>}
        {bid.deliverables && !isHamper && <p><span className="text-muted-foreground">Deliverables: </span>{bid.deliverables}</p>}
        {bid.content_concept && <p><span className="text-muted-foreground">Concept: </span>{bid.content_concept}</p>}
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground pt-1">
          {bid.estimated_reach && <span>Reach: {bid.estimated_reach}</span>}
          {bid.engagement_stats && <span>Engagement: {bid.engagement_stats}</span>}
          {bid.timeline && <span>Timeline: {bid.timeline}</span>}
        </div>
        {bid.note && <p className="text-muted-foreground italic pt-1">"{bid.note}"</p>}
      </div>
      {isPromoter && campaign.status === "open" && (!isHamper || bid.status === "pending") && (
        <div className="mt-4 flex gap-2">
          <Button onClick={accept} disabled={accepting} data-testid={`accept-bid-${bid.bid_id}`} className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-semibold h-9"><CheckCircle2 className="h-4 w-4 mr-1" /> {accepting ? "Saving…" : isHamper ? "Accept" : "Select this creator"}</Button>
          {isHamper && <Button variant="outline" onClick={() => act("reject")} disabled={accepting} data-testid={`reject-bid-${bid.bid_id}`} className="rounded-full h-9">Decline</Button>}
        </div>
      )}
      {bid.status === "accepted" && <div className="mt-4 flex items-center gap-2 text-neon-lime text-sm font-semibold"><Trophy className="h-4 w-4" /> {isHamper ? "Accepted for hamper" : "Selected creator"}</div>}
    </motion.div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  const isPromoter = user?.role === "promoter";

  const load = async () => {
    try {
      const c = await api.get(`/campaigns/${id}`);
      setCampaign(c.data);
      if (user?.role === "promoter" && c.data.promoter_id === user.user_id) {
        const b = await api.get(`/campaigns/${id}/bids`);
        setBids(b.data);
      }
    } catch (e) {
      toast.error("Campaign not found.");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <AppShell><p className="text-muted-foreground">Loading…</p></AppShell>;
  if (!campaign) return null;

  const ownCampaign = isPromoter && campaign.promoter_id === user.user_id;
  const isHamper = campaign.compensation_type === "gift_hamper";
  const closeCampaign = async () => {
    try { await api.post(`/campaigns/${campaign.campaign_id}/close`); toast.success("Applications closed."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not close campaign."); }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* main */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Badge variant="outline" className={`capitalize ${statusColors[campaign.status]}`}>{campaign.status}</Badge>
              {campaign.category && <Badge variant="outline" className="border-neon-lime/30 text-neon-lime bg-neon-lime/10">{campaign.category}</Badge>}
              {campaign.campaign_type && <Badge variant="outline" className="border-border text-muted-foreground">{campaign.campaign_type}</Badge>}
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-3">{campaign.title}</h1>
            <div className="flex items-center gap-3 mb-6">
              <img src={campaign.promoter_picture} alt="" className="h-8 w-8 rounded-full object-cover bg-muted" />
              <span className="text-sm text-muted-foreground">by {campaign.promoter_name}</span>
            </div>

            <div className="rounded-2xl border border-border bg-surface p-6">
              <DetailRow icon={Target} label="Objective" value={campaign.objective} />
              <DetailRow icon={Package} label="Product / service" value={campaign.product} />
              <DetailRow icon={Layers} label="Deliverables" value={campaign.deliverables} />
              <DetailRow icon={Users} label="Target audience" value={campaign.target_audience} />
              <DetailRow icon={MapPin} label="Location" value={campaign.location} />
              <DetailRow icon={Clock} label="Timeline" value={campaign.timeline} />
              <DetailRow icon={Info} label="Special instructions" value={campaign.special_instructions} />
              {campaign.platforms?.length > 0 && (
                <div className="flex gap-3 py-3 border-t border-border mt-1">
                  <Layers className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5">Platforms</p>
                    <div className="flex flex-wrap gap-2">{campaign.platforms.map((p) => <span key={p} className="text-xs px-2.5 py-1 rounded-full bg-background border border-border">{p}</span>)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* sidebar */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-border bg-surface p-6 sticky top-6">
              {campaign.compensation_type === "gift_hamper" ? (
                <>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">🎁 Gift hamper</p>
                  <p className="font-heading font-black text-4xl mt-1 mb-1">{formatMoney(campaign.hamper_value, campaign.currency)}</p>
                  <p className="text-sm text-muted-foreground mb-2">value · {campaign.hamper_influencers_required} influencer(s) needed</p>
                  <p className="text-sm font-semibold mb-2" data-testid="hamper-accepted-count">{campaign.hamper_accepted_count || 0} / {campaign.hamper_influencers_required} accepted · {campaign.bids_count} applied</p>
                  {campaign.hamper_items && <p className="text-sm text-muted-foreground mb-6"><span className="font-semibold text-foreground">Includes:</span> {campaign.hamper_items}</p>}
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Maximum budget</p>
                  <p className="font-heading font-black text-4xl mt-1 mb-1">{formatMoney(campaign.budget, campaign.currency)}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-6"><Gavel className="h-4 w-4" /> {campaign.bids_count} bids received</p>
                </>
              )}

              {!isPromoter && campaign.status === "open" && !campaign.my_bid && (isHamper ? <HamperApplyForm campaign={campaign} onSubmitted={load} /> : <BidForm campaign={campaign} onSubmitted={load} />)}
              {!isPromoter && campaign.my_bid && (
                <div className="rounded-xl border border-neon-cyan/30 bg-neon-cyan/5 p-4 text-sm" data-testid="my-bid-status">
                  <p className="font-semibold text-neon-cyan flex items-center gap-1 mb-1"><CheckCircle2 className="h-4 w-4" /> {isHamper ? "Application sent" : "Bid submitted"}</p>
                  <p className="text-muted-foreground">{isHamper ? "Gift hamper" : `You bid ${formatMoney(campaign.my_bid.amount, campaign.my_bid.currency)}`} · <span className="capitalize">{campaign.my_bid.status}</span></p>
                </div>
              )}
              {!isPromoter && campaign.status !== "open" && !campaign.my_bid && (
                <p className="text-sm text-muted-foreground">This campaign is no longer accepting bids.</p>
              )}
              {isPromoter && !ownCampaign && <p className="text-sm text-muted-foreground">This is another promoter's campaign.</p>}
              {ownCampaign && isHamper && campaign.status === "open" && (
                <Button variant="outline" onClick={closeCampaign} data-testid="close-campaign-btn" className="w-full rounded-full h-10">Close applications</Button>
              )}
              {ownCampaign && ["awarded", "completed"].includes(campaign.status) && (
                <div className="rounded-xl border border-neon-lime/30 bg-neon-lime/5 p-4 text-sm">
                  <p className="font-semibold text-neon-lime flex items-center gap-1"><Trophy className="h-4 w-4" /> {isHamper ? "Applications closed" : "Creator selected"}</p>
                </div>
              )}
              {!isHamper && campaign.selected_bid_id && (
                <EscrowPanel campaign={campaign} isOwner={ownCampaign} isSelected={campaign.selected_influencer_id === user.user_id} onChange={load} />
              )}
            </div>
          </div>
        </div>

        {/* Bids list for promoter */}
        {ownCampaign && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-2xl mb-5 flex items-center gap-2"><Gavel className="h-6 w-6 text-neon-pink" /> {isHamper ? "Applications" : "Bids received"} ({bids.length})</h2>
            {bids.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-surface p-10 text-center">
                <p className="text-muted-foreground">No bids yet. Creators will appear here as they submit proposals.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {bids.map((b, i) => <BidCard key={b.bid_id} bid={b} campaign={campaign} onAccept={load} isPromoter={isPromoter} i={i} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
